import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { DocumentRequirements } from '@/hooks/usePartnerSettings';

interface RequirementsConfiguratorProps {
  category: string;
  requirements: DocumentRequirements;
  onChange: (requirements: DocumentRequirements) => void;
}

export function RequirementsConfigurator({ category, requirements, onChange }: RequirementsConfiguratorProps) {
  const updateRequirement = (field: string, value: any) => {
    onChange({ ...requirements, [field]: value });
  };

  const renderInsuranceRequirements = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Coverage Minimums</CardTitle>
          <CardDescription>Minimum coverage amounts required (USD)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="min_per_occurrence">Per Occurrence</Label>
              <Input
                id="min_per_occurrence"
                type="number"
                value={requirements.min_per_occurrence || ''}
                onChange={(e) => updateRequirement('min_per_occurrence', parseInt(e.target.value) || 0)}
                placeholder="1000000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="min_aggregate">Aggregate</Label>
              <Input
                id="min_aggregate"
                type="number"
                value={requirements.min_aggregate || ''}
                onChange={(e) => updateRequirement('min_aggregate', parseInt(e.target.value) || 0)}
                placeholder="2000000"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Required Endorsements</CardTitle>
          <CardDescription>Additional insured and waiver requirements</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Additional Insured</Label>
              <p className="text-xs text-muted-foreground">Certificate must name additional insured</p>
            </div>
            <Switch
              checked={requirements.additional_insured_required || false}
              onCheckedChange={(checked) => updateRequirement('additional_insured_required', checked)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Waiver of Subrogation</Label>
              <p className="text-xs text-muted-foreground">Waiver endorsement required</p>
            </div>
            <Switch
              checked={requirements.waiver_of_subrogation_required || false}
              onCheckedChange={(checked) => updateRequirement('waiver_of_subrogation_required', checked)}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Primary & Non-Contributory</Label>
              <p className="text-xs text-muted-foreground">Primary coverage endorsement</p>
            </div>
            <Switch
              checked={requirements.primary_noncontributory_required || false}
              onCheckedChange={(checked) => updateRequirement('primary_noncontributory_required', checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Carrier Requirements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="min_am_best_rating">Minimum AM Best Rating</Label>
            <Input
              id="min_am_best_rating"
              value={requirements.min_am_best_rating || ''}
              onChange={(e) => updateRequirement('min_am_best_rating', e.target.value)}
              placeholder="A-"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Admitted Carrier Required</Label>
            <Switch
              checked={requirements.admitted_carrier_required || false}
              onCheckedChange={(checked) => updateRequirement('admitted_carrier_required', checked)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderLicenseRequirements = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">License Validation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Active Status Required</Label>
              <p className="text-xs text-muted-foreground">License must be in active/current status</p>
            </div>
            <Switch
              checked={requirements.active_status_required || false}
              onCheckedChange={(checked) => updateRequirement('active_status_required', checked)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="min_monetary_limit">Minimum Monetary Limit</Label>
            <Input
              id="min_monetary_limit"
              type="number"
              value={requirements.min_monetary_limit || ''}
              onChange={(e) => updateRequirement('min_monetary_limit', parseInt(e.target.value) || 0)}
              placeholder="500000"
            />
            <p className="text-xs text-muted-foreground">Leave blank for no minimum</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderFinancialRequirements = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bonding Requirements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="min_single_project_limit">Minimum Single Project Limit</Label>
            <Input
              id="min_single_project_limit"
              type="number"
              value={requirements.min_single_project_limit || ''}
              onChange={(e) => updateRequirement('min_single_project_limit', parseInt(e.target.value) || 0)}
              placeholder="5000000"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="min_aggregate_capacity">Minimum Aggregate Capacity</Label>
            <Input
              id="min_aggregate_capacity"
              type="number"
              value={requirements.min_aggregate_capacity || ''}
              onChange={(e) => updateRequirement('min_aggregate_capacity', parseInt(e.target.value) || 0)}
              placeholder="10000000"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSafetyRequirements = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Safety Standards</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="max_emr">Maximum EMR (Experience Modification Rate)</Label>
            <Input
              id="max_emr"
              type="number"
              step="0.1"
              value={requirements.max_emr || ''}
              onChange={(e) => updateRequirement('max_emr', parseFloat(e.target.value) || 0)}
              placeholder="1.0"
            />
            <p className="text-xs text-muted-foreground">Industry standard is 1.0 or below</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderLegalRequirements = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tax Document Requirements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>TIN/EIN Required</Label>
              <p className="text-xs text-muted-foreground">Must include valid tax identification</p>
            </div>
            <Switch
              checked={requirements.tin_required || false}
              onCheckedChange={(checked) => updateRequirement('tin_required', checked)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="max_age_days">Maximum Document Age (days)</Label>
            <Input
              id="max_age_days"
              type="number"
              value={requirements.max_age_days || ''}
              onChange={(e) => updateRequirement('max_age_days', parseInt(e.target.value) || 0)}
              placeholder="365"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Common Requirements for All Types */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">General Requirements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="min_days_until_expiry">Minimum Days Until Expiry</Label>
            <Input
              id="min_days_until_expiry"
              type="number"
              value={requirements.min_days_until_expiry || ''}
              onChange={(e) => updateRequirement('min_days_until_expiry', parseInt(e.target.value) || 0)}
              placeholder="30"
            />
            <p className="text-xs text-muted-foreground">
              Document must be valid for at least this many days
            </p>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Category-Specific Requirements */}
      {category === 'Insurance' && renderInsuranceRequirements()}
      {category === 'License' && renderLicenseRequirements()}
      {category === 'Financial' && renderFinancialRequirements()}
      {category === 'Safety' && renderSafetyRequirements()}
      {category === 'Legal' && renderLegalRequirements()}
    </div>
  );
}
